import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { collection, doc, getDoc, getDocs, limit, orderBy, query, updateDoc, where, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, deleteObject } from 'firebase/storage';
import {
  PortvilleRoomWithResidency,
  PortvilleRoomFormData,
  PortvilleRoommateFormData,
  ROOM_STATUS_LABELS
} from '../../../types/portvilleRoom';
import { db, storage } from '../../../firebase';
import { downloadStorageFile } from '../../../utils/storageDownload';
import { MAX_PORTVILLE_ROOMMATES } from '../../../utils/portvilleRoommates';

interface RoomFormProps {
  room?: PortvilleRoomWithResidency;
  presetStatus?: PortvilleRoomWithResidency['status'];
  onSubmit: (formData: PortvilleRoomFormData) => Promise<boolean>;
  onCancel: () => void;
  onViewContract?: (room: PortvilleRoomWithResidency, contractType?: 'original' | 'extension1' | 'extension2') => void;
  onMoveOutAction?: (action: 'detail' | 'approve' | 'reject', requestId: string) => void;
  isSubmitting: boolean;
  processingMoveOut?: boolean;
}

type UserProfileOption = {
  uid: string;
  name: string;
  email?: string;
  phoneNumber?: string;
  organization?: string;
};

type DocumentType = 'identification' | 'employment' | 'other';
type Documents = Partial<Record<DocumentType, string>>;

interface MoveInInspectionAttachment {
  id: string;
  name: string;
  storagePath: string;
  size: number;
  contentType?: string;
  uploadedAt?: Timestamp;
}

interface MoveInInspectionRecord {
  id: string;
  roomId: string;
  roomNumber?: string;
  residencyId?: string;
  residentUid?: string;
  inspectorName?: string;
  inspectionItems: Array<{
    id: string;
    name: string;
    checked: boolean;
    notes?: string;
  }>;
  inspectionNotes?: string;
  attachments: MoveInInspectionAttachment[];
  signatureUrl?: string;
  inspectionSignedAt?: Date;
  createdAt?: Date;
}

const getDocumentDisplayName = (docType: DocumentType) => {
  switch (docType) {
    case 'identification':
      return '신분증 사본';
    case 'employment':
      return '재직증명서/사업자등록증';
    case 'other':
      return '기타 서류';
  }
};

const getOriginalFileNameFromStoragePath = (storagePath: string) => {
  const fileName = storagePath.split('/').pop() || storagePath;
  const extracted = fileName.split('_').slice(3).join('_');
  return extracted || fileName;
};

const toOptionalDate = (value: any): Date | undefined => {
  if (!value) return undefined;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

const getMillis = (value: any): number => toOptionalDate(value)?.getTime() || 0;

const validateUploadFile = (file: File): string | null => {
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  if (file.size > MAX_FILE_SIZE) {
    return `파일 크기는 5MB를 초과할 수 없습니다. (현재: ${(file.size / 1024 / 1024).toFixed(2)}MB)`;
  }

  const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
  if (!allowedTypes.includes(file.type)) {
    return `허용되지 않는 파일 형식입니다. PDF, JPG, PNG 파일만 업로드할 수 있습니다. (현재: ${file.type})`;
  }

  const fileName = file.name.toLowerCase();
  const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];
  const hasValidExtension = allowedExtensions.some((ext) => fileName.endsWith(ext));
  if (!hasValidExtension) {
    return '허용되지 않는 파일 확장자입니다. .pdf, .jpg, .jpeg, .png 파일만 업로드할 수 있습니다.';
  }

  return null;
};

const RoomForm: React.FC<RoomFormProps> = ({ room, presetStatus, onSubmit, onCancel, onViewContract, onMoveOutAction, isSubmitting, processingMoveOut = false }) => {
  const [formData, setFormData] = useState<PortvilleRoomFormData>({
    roomNumber: '',
    roomType: '호실',
    status: 'vacant',
    residentUid: undefined,
    residentName: '',
    residentPhoneNumber: '',
    residentOrganization: '',
    startDate: '',
    endDate: '',
    hasManualExtension1: false,
    originalContractEndDate: '',
    roommates: [],
    roommateInfo: '',
    roommateContact: '',
    vehicleInfo: '',
    notes: ''
  });

  const [residentInputMode, setResidentInputMode] = useState<'manual' | 'select'>('manual');
  const [userProfiles, setUserProfiles] = useState<UserProfileOption[]>([]);
  const [userProfilesLoading, setUserProfilesLoading] = useState(false);
  const [userProfilesError, setUserProfilesError] = useState<string | null>(null);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [lotteryDocuments, setLotteryDocuments] = useState<Documents>({});
  const [lotteryDocumentsLoading, setLotteryDocumentsLoading] = useState(false);
  const [lotteryDocumentsError, setLotteryDocumentsError] = useState<string | null>(null);
  const [lotteryDocumentsDownloading, setLotteryDocumentsDownloading] = useState<DocumentType | null>(null);
  const [lotteryDocumentsUploading, setLotteryDocumentsUploading] = useState<DocumentType | null>(null);
  const [lotteryDocMeta, setLotteryDocMeta] = useState<{ id: string; uid: string } | null>(null);
  const [extensionDocumentsError, setExtensionDocumentsError] = useState<string | null>(null);
  const [extensionDocumentsDownloading, setExtensionDocumentsDownloading] = useState<{
    round: 1 | 2;
    type: DocumentType;
  } | null>(null);
  const [extensionDocumentsUploading, setExtensionDocumentsUploading] = useState<{
    round: 1 | 2;
    type: DocumentType;
  } | null>(null);
  const [extensionDocumentsRound1, setExtensionDocumentsRound1] = useState<Documents>({});
  const [extensionDocumentsRound2, setExtensionDocumentsRound2] = useState<Documents>({});
  const [markingExtensionApplied, setMarkingExtensionApplied] = useState<1 | 2 | null>(null);
  const [moveInInspectionRecords, setMoveInInspectionRecords] = useState<MoveInInspectionRecord[]>([]);
  const [moveInInspectionLoading, setMoveInInspectionLoading] = useState(false);
  const [moveInInspectionError, setMoveInInspectionError] = useState<string | null>(null);
  const [moveInInspectionDownloadingAttachmentId, setMoveInInspectionDownloadingAttachmentId] = useState<string | null>(null);
  const [activeRoommateIndex, setActiveRoommateIndex] = useState(0);
  const [roommateAttachmentError, setRoommateAttachmentError] = useState<string | null>(null);
  const [roommateAttachmentDownloadingIndex, setRoommateAttachmentDownloadingIndex] = useState<number | null>(null);

  useEffect(() => {
    if (room) {
      // 디버깅: 연장계약 관련 정보 로그
      console.log('RoomForm - room 데이터:', {
        roomNumber: room.roomNumber,
        contractSignedAt: room.contractSignedAt,
        extensionContractSignedAt: room.extensionContractSignedAt,
        extensionStatus: room.extensionStatus,
        hasExtended: room.hasExtended,
        originalEndDate: room.originalEndDate
      });
      
      const nextFormData: PortvilleRoomFormData = {
        roomNumber: room.roomNumber,
        roomType: room.roomType || '호실',
        status: room.status as 'occupied' | 'preparing' | 'vacant' | 'contracting',
        residentUid: room.residentUid,
        residentName: room.currentResident?.name || '',
        residentPhoneNumber: room.currentResident?.phoneNumber || '',
        residentOrganization: room.currentResident?.organization || '',
        startDate: room.occupancyPeriod?.startDate ? 
          room.occupancyPeriod.startDate.toISOString().split('T')[0] : '',
        endDate: room.occupancyPeriod?.endDate ? 
          room.occupancyPeriod.endDate.toISOString().split('T')[0] : '',
        hasManualExtension1: Boolean(room.hasExtended || room.extensionStatus === 'contracted'),
        originalContractEndDate: room.originalEndDate ? room.originalEndDate.toISOString().split('T')[0] : '',
        roommates: (room.roommates || []).map((roommate) => ({ ...roommate })),
        roommateInfo: room.roommateInfo || '',
        roommateContact: room.roommateContact || '',
        vehicleInfo: room.vehicleInfo || '',
        notes: room.notes || ''
      };

      const canPresetToContracting =
        presetStatus === 'contracting' &&
        !room.currentResidencyId &&
        (room.status === 'vacant' || room.status === 'preparing');

      if (canPresetToContracting) {
        nextFormData.status = 'contracting';
        if (!nextFormData.startDate) {
          const start = new Date();
          nextFormData.startDate = start.toISOString().split('T')[0];
        }
        if (!nextFormData.endDate) {
          const start = new Date(nextFormData.startDate);
          const end = new Date(start);
          end.setDate(end.getDate() + 729);
          nextFormData.endDate = end.toISOString().split('T')[0];
        }
      }

      const canPresetToOccupied =
        presetStatus === 'occupied' &&
        !room.currentResidencyId &&
        (room.status === 'vacant' || room.status === 'preparing');

      if (canPresetToOccupied) {
        nextFormData.status = 'occupied';
        if (!nextFormData.startDate) {
          const start = new Date();
          nextFormData.startDate = start.toISOString().split('T')[0];
        }
        if (!nextFormData.endDate) {
          const start = new Date(nextFormData.startDate);
          const end = new Date(start);
          end.setDate(end.getDate() + 729);
          nextFormData.endDate = end.toISOString().split('T')[0];
        }
      }

      setFormData(nextFormData);
      setActiveRoommateIndex(0);
      
      setResidentInputMode(room.residentUid ? 'select' : 'manual');
    } else {
      setActiveRoommateIndex(0);
    }
  }, [room, presetStatus]);

  useEffect(() => {
    setExtensionDocumentsRound1((room?.extensionDocuments || {}) as Documents);
    setExtensionDocumentsRound2((room?.secondExtensionDocuments || {}) as Documents);
  }, [room?.extensionDocuments, room?.secondExtensionDocuments]);

  useEffect(() => {
    let cancelled = false;

    const loadMoveInInspections = async () => {
      if (!room?.id || room.roomType === '시설물' || !room.currentResidencyId || !room.residentUid) {
        setMoveInInspectionRecords([]);
        setMoveInInspectionError(null);
        setMoveInInspectionLoading(false);
        return;
      }

      try {
        setMoveInInspectionLoading(true);
        setMoveInInspectionError(null);

        const snapshot = await getDocs(query(collection(db, 'portville_inspections'), where('roomId', '==', room.id)));
        const records = snapshot.docs
          .map((inspectionDoc) => {
            const data = inspectionDoc.data() as any;
            return {
              id: inspectionDoc.id,
              roomId: data.roomId || room.id,
              roomNumber: data.roomNumber,
              residencyId: data.residencyId,
              residentUid: data.residentUid,
              inspectorName: data.inspectorName,
              inspectionItems: Array.isArray(data.inspectionItems) ? data.inspectionItems : [],
              inspectionNotes: data.inspectionNotes || '',
              attachments: Array.isArray(data.attachments) ? data.attachments : [],
              signatureUrl: data.signatureUrl || '',
              inspectionSignedAt: toOptionalDate(data.inspectionSignedAt),
              createdAt: toOptionalDate(data.createdAt)
            } as MoveInInspectionRecord;
          })
          .filter((record) => {
            const raw = snapshot.docs.find((inspectionDoc) => inspectionDoc.id === record.id)?.data() as any;
            const isMoveInInspection = !raw?.inspectionType || raw.inspectionType === 'move-in';
            return (
              isMoveInInspection &&
              record.residencyId === room.currentResidencyId &&
              record.residentUid === room.residentUid
            );
          })
          .sort((a, b) => getMillis(b.inspectionSignedAt || b.createdAt) - getMillis(a.inspectionSignedAt || a.createdAt));

        if (!cancelled) {
          setMoveInInspectionRecords(records);
        }
      } catch (error) {
        console.warn('입주점검 기록 조회 실패:', error);
        if (!cancelled) {
          setMoveInInspectionError('입주점검 기록을 불러오지 못했습니다.');
        }
      } finally {
        if (!cancelled) {
          setMoveInInspectionLoading(false);
        }
      }
    };

    void loadMoveInInspections();
    return () => {
      cancelled = true;
    };
  }, [room?.id, room?.roomType, room?.currentResidencyId, room?.residentUid]);

	  useEffect(() => {
	    let cancelled = false;

	    const loadLotteryDocuments = async () => {
	      if (!room?.id || room.roomType === '시설물') return;
	      if (room.status === 'preparing') {
	        setLotteryDocumentsLoading(false);
	        setLotteryDocumentsError(null);
	        setLotteryDocuments({});
	        setLotteryDocMeta(null);
	        return;
	      }

	      try {
	        setLotteryDocumentsLoading(true);
	        setLotteryDocumentsError(null);
	        setLotteryDocuments({});
	        setLotteryDocMeta(null);

        const lotteryRef = collection(db, 'portville_lottery_history');
        const snapshot = await getDocs(query(lotteryRef, where('roomId', '==', room.id)));

	        const docs = snapshot.docs
	          .map((d) => ({ id: d.id, data: d.data() as any }))
	          .filter((d) => Boolean(d.data))
	          .sort((a, b) => getMillis(b.data.drawnAt) - getMillis(a.data.drawnAt));

	        const contractorUid = room.residentUid || '';
	        const byUid = contractorUid ? docs.filter((d) => String(d.data.uid || '') === String(contractorUid)) : [];
	        const selected = (byUid.length > 0 ? byUid : docs)[0] || null;
	        const documents = (selected?.data?.documents || {}) as Documents;

	        if (!cancelled) {
	          setLotteryDocuments(documents);
          const uid = selected?.data?.uid || contractorUid || '';
          setLotteryDocMeta(selected ? { id: selected.id, uid } : null);
        }
      } catch (error) {
        console.warn('계약 전 제출 서류 조회 실패:', error);
        if (!cancelled) {
          setLotteryDocumentsError('계약 전 제출 서류를 불러오지 못했습니다.');
        }
      } finally {
        if (!cancelled) {
          setLotteryDocumentsLoading(false);
        }
      }
    };

	    void loadLotteryDocuments();
	    return () => {
	      cancelled = true;
	    };
	  }, [room?.id, room?.residentUid, room?.roomType, room?.status]);

  const loadUserProfiles = useCallback(async () => {
    if (userProfilesLoading) return;

    setUserProfilesLoading(true);
    setUserProfilesError(null);

    try {
      const profilesQuery = query(
        collection(db, 'user_profiles'),
        orderBy('name'),
        limit(500)
      );
      const snapshot = await getDocs(profilesQuery);
      const profiles: UserProfileOption[] = snapshot.docs.map((docSnap) => {
        const data: any = docSnap.data();
        return {
          uid: docSnap.id,
          name: data?.name || '',
          email: data?.email || '',
          phoneNumber: data?.phoneNumber,
          organization: data?.organization
        };
      }).filter(p => p.uid && p.name);

      setUserProfiles(profiles);
    } catch (error) {
      console.error('사용자 목록 조회 실패:', error);
      setUserProfilesError('사용자 목록을 불러오지 못했습니다.');
    } finally {
      setUserProfilesLoading(false);
    }
  }, [userProfilesLoading]);

  const loadUserProfileByUid = useCallback(async (uid: string) => {
    if (!uid || userProfiles.some((profile) => profile.uid === uid)) return;

    try {
      const profileDoc = await getDoc(doc(db, 'user_profiles', uid));
      if (!profileDoc.exists()) return;

      const data: any = profileDoc.data();
      const profile: UserProfileOption = {
        uid: profileDoc.id,
        name: data?.name || '',
        email: data?.email || '',
        phoneNumber: data?.phoneNumber,
        organization: data?.organization
      };

      if (profile.name) {
        setUserProfiles((prev) => (
          prev.some((item) => item.uid === profile.uid) ? prev : [...prev, profile]
        ));
      }
    } catch (error) {
      console.error('선택된 사용자 프로필 조회 실패:', error);
    }
  }, [userProfiles]);

  useEffect(() => {
    if (residentInputMode === 'select' && userProfiles.length === 0) {
      void loadUserProfiles();
    }
  }, [residentInputMode, userProfiles.length, loadUserProfiles]);

  useEffect(() => {
    if (residentInputMode === 'select' && formData.residentUid) {
      void loadUserProfileByUid(formData.residentUid);
    }
  }, [residentInputMode, formData.residentUid, loadUserProfileByUid]);

  const filteredUserProfiles = useMemo(() => {
    const keyword = userSearchTerm.trim().toLowerCase();
    if (!keyword) return userProfiles;

    return userProfiles.filter((profile) => {
      const fields = [
        profile.name,
        profile.email,
        profile.phoneNumber,
        profile.organization,
        profile.uid
      ].filter(Boolean) as string[];
      return fields.some((field) => field.toLowerCase().includes(keyword));
    });
  }, [userProfiles, userSearchTerm]);

  const selectedUserProfile = useMemo(() => {
    if (!formData.residentUid) return null;
    return userProfiles.find((profile) => profile.uid === formData.residentUid) || null;
  }, [userProfiles, formData.residentUid]);

  const handleResidentInputModeChange = (mode: 'manual' | 'select') => {
    setResidentInputMode(mode);

    if (mode === 'manual') {
      setFormData(prev => ({ ...prev, residentUid: undefined }));
      return;
    }

    if (userProfiles.length === 0) {
      void loadUserProfiles();
    }
  };

  const handleSelectResidentUid = (uid: string) => {
    const selected = userProfiles.find((p) => p.uid === uid);
    setFormData(prev => ({
      ...prev,
      residentUid: uid || undefined,
      residentName: selected?.name ?? prev.residentName,
      residentPhoneNumber: selected?.phoneNumber ?? prev.residentPhoneNumber,
      residentOrganization: selected?.organization ?? prev.residentOrganization
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const roommates = formData.roommates || [];
    const shouldValidateRoommates =
      formData.status === 'occupied' || formData.status === 'contracting';
    const invalidRoommateIndex = shouldValidateRoommates
      ? roommates.findIndex(
          (roommate) => !roommate.name.trim() || !roommate.contact.trim()
        )
      : -1;
    if (invalidRoommateIndex >= 0) {
      setActiveRoommateIndex(invalidRoommateIndex);
      setRoommateAttachmentError('모든 동거인의 성명과 연락처를 입력해주세요.');
      return;
    }

    if (formData.hasManualExtension1) {
      if (!formData.startDate || !formData.endDate || !formData.originalContractEndDate) {
        alert('1차 연장 반영 시 입주 개시일, 최초 계약 종료일, 현재 계약 종료일을 모두 입력해주세요.');
        return;
      }

      const originalEndDate = new Date(formData.originalContractEndDate);
      const currentEndDate = new Date(formData.endDate);
      if (originalEndDate >= currentEndDate) {
        alert('최초 계약 종료일은 현재 계약 종료일보다 이전이어야 합니다.');
        return;
      }
    }

    const normalizedFormData: PortvilleRoomFormData = {
      ...formData,
      residentUid: formData.residentUid?.trim() ? formData.residentUid.trim() : undefined,
      roommates: roommates.map((roommate) => ({
        ...roommate,
        name: roommate.name.trim(),
        contact: roommate.contact.trim()
      })),
      roommateInfo: roommates[0]?.name.trim() || '',
      roommateContact: roommates[0]?.contact.trim() || ''
    };
    const success = await onSubmit(normalizedFormData);
    if (success) {
      if (!room) {
        // 새 호실 추가인 경우 폼 초기화
        setFormData({
          roomNumber: '',
          roomType: '호실',
          status: 'vacant',
          residentUid: undefined,
          residentName: '',
          residentPhoneNumber: '',
          residentOrganization: '',
          startDate: '',
          endDate: '',
          hasManualExtension1: false,
          originalContractEndDate: '',
          roommates: [],
          roommateInfo: '',
          roommateContact: '',
          vehicleInfo: '',
          notes: ''
        });
        setActiveRoommateIndex(0);
      }
    }
  };

  const handleInputChange = (field: keyof PortvilleRoomFormData, value: string) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };

      if (field === 'status' && value !== 'occupied') {
        next.hasManualExtension1 = false;
        next.originalContractEndDate = '';
      }

      return next;
    });
  };

  const updateRoommate = (
    index: number,
    updates: Partial<PortvilleRoommateFormData>
  ) => {
    setRoommateAttachmentError(null);
    setFormData((prev) => {
      const roommates = [...(prev.roommates || [])];
      roommates[index] = { ...roommates[index], ...updates };
      return { ...prev, roommates };
    });
  };

  const addRoommate = () => {
    const currentCount = formData.roommates?.length || 0;
    if (currentCount >= MAX_PORTVILLE_ROOMMATES) return;

    setFormData((prev) => ({
      ...prev,
      roommates: [
        ...(prev.roommates || []),
        { name: '', contact: '' }
      ]
    }));
    setActiveRoommateIndex(currentCount);
    setRoommateAttachmentError(null);
  };

  const removeRoommate = (index: number) => {
    const roommate = formData.roommates?.[index];
    if (!roommate) return;

    if (roommate.attachment || roommate.pendingAttachmentFile) {
      setActiveRoommateIndex(index);
      setRoommateAttachmentError(
        roommate.pendingAttachmentFile
          ? '선택한 첨부파일을 먼저 취소한 후 동거인을 삭제해주세요.'
          : '등록된 첨부파일을 먼저 삭제한 후 동거인을 삭제해주세요.'
      );
      return;
    }

    const hasData = Boolean(
      roommate.name.trim() ||
      roommate.contact.trim()
    );
    if (
      hasData &&
      !window.confirm(
        `${roommate.name.trim() || `동거인 ${index + 1}`} 정보를 삭제하시겠습니까?`
      )
    ) {
      return;
    }

    setFormData((prev) => {
      const roommates = [...(prev.roommates || [])];
      roommates.splice(index, 1);
      return { ...prev, roommates };
    });
    setActiveRoommateIndex((current) => {
      const nextCount = Math.max(0, (formData.roommates?.length || 1) - 1);
      return Math.min(current > index ? current - 1 : current, Math.max(0, nextCount - 1));
    });
    setRoommateAttachmentError(null);
  };

  const selectRoommateAttachment = (index: number, file: File) => {
    const validationError = validateUploadFile(file);
    if (validationError) {
      setRoommateAttachmentError(validationError);
      return;
    }

    updateRoommate(index, { pendingAttachmentFile: file });
  };

  const removeRoommateAttachment = (index: number) => {
    const roommate = formData.roommates?.[index];
    if (!roommate) return;
    if (roommate.pendingAttachmentFile) {
      updateRoommate(index, { pendingAttachmentFile: undefined });
      return;
    }
    if (
      roommate.attachment &&
      !window.confirm('이 동거인의 첨부파일을 삭제하시겠습니까?')
    ) {
      return;
    }

    updateRoommate(index, {
      attachment: undefined,
      pendingAttachmentFile: undefined
    });
  };

  const downloadRoommateAttachment = async (index: number) => {
    const attachment = formData.roommates?.[index]?.attachment;
    if (!attachment) return;

    try {
      setRoommateAttachmentDownloadingIndex(index);
      setRoommateAttachmentError(null);
      await downloadStorageFile(
        attachment.storagePath,
        `${room?.roomNumber || formData.roomNumber || ''}호_동거인${index + 1}_${attachment.name.replace(/\.[^/.]+$/, '')}`
      );
    } catch (downloadError) {
      console.error('동거인 첨부파일 다운로드 실패:', downloadError);
      setRoommateAttachmentError('동거인 첨부파일 다운로드에 실패했습니다.');
    } finally {
      setRoommateAttachmentDownloadingIndex(null);
    }
  };

  const handleManualExtensionToggle = (checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      hasManualExtension1: checked,
      originalContractEndDate: checked ? prev.originalContractEndDate : ''
    }));
  };

  const isOccupied = formData.status === 'occupied';
  const isContracting = formData.status === 'contracting';
  const showResidentInfo = isOccupied || isContracting;
  const hasCurrentResidency = Boolean(room?.currentResidencyId && room.roomType !== '시설물');
  const residentNameForFiles = room?.currentResident?.name || formData.residentName || '입주자';
  const extensionEditingLocked = Boolean(
    room?.extensionContractSignedAt ||
      room?.secondExtensionContractSignedAt ||
      room?.secondExtensionStatus === 'applied' ||
      room?.secondExtensionStatus === 'approved' ||
      room?.secondExtensionStatus === 'contracted'
  );

  const showExtensionDocumentsSection = Boolean(
    room &&
      (room.extensionDocumentsUpdatedAt ||
        room.extensionStatus === 'applied' ||
        room.extensionStatus === 'approved' ||
        room.extensionStatus === 'contracted' ||
        room.hasExtended ||
        room.extensionContractSignedAt ||
        Object.values(extensionDocumentsRound1 || {}).some(Boolean))
  );
  const showSecondExtensionDocumentsSection = Boolean(
    room &&
      (room.secondExtensionDocumentsUpdatedAt ||
        room.secondExtensionStatus === 'applied' ||
        room.secondExtensionStatus === 'approved' ||
        room.secondExtensionStatus === 'contracted' ||
        room.hasSecondExtended ||
        room.secondExtensionContractSignedAt ||
        Object.values(extensionDocumentsRound2 || {}).some(Boolean))
  );

  const canMarkFirstExtensionApplied = Boolean(
    room?.currentResidencyId &&
      room.status === 'occupied' &&
      !room.extensionContractSignedAt &&
      room.extensionStatus !== 'applied' &&
      room.extensionStatus !== 'approved' &&
      room.extensionStatus !== 'contracted'
  );
  const canMarkSecondExtensionApplied = Boolean(
    room?.currentResidencyId &&
      room.status === 'occupied' &&
      (room.hasExtended || room.extensionContractSignedAt || room.extensionStatus === 'contracted') &&
      !room.secondExtensionContractSignedAt &&
      room.secondExtensionStatus !== 'applied' &&
      room.secondExtensionStatus !== 'approved' &&
      room.secondExtensionStatus !== 'contracted'
  );

  const handleMarkExtensionApplied = async (round: 1 | 2) => {
    if (!room?.currentResidencyId) return;

    const confirmed = window.confirm(
      `${round}차 계약 연장 신청 상태로 변경하시겠습니까?\n관리자가 사용자를 대신해 연장 신청을 접수한 것으로 처리됩니다.`
    );
    if (!confirmed) return;

    try {
      setMarkingExtensionApplied(round);

      const statusKey = round === 2 ? 'secondExtensionStatus' : 'extensionStatus';
      const appliedAtKey = round === 2 ? 'secondExtensionAppliedAt' : 'extensionAppliedAt';
      const originalEndDateKey = round === 2 ? 'secondOriginalEndDate' : 'originalEndDate';
      const originalEndDateAlreadySet = round === 2 ? Boolean(room.secondOriginalEndDate) : Boolean(room.originalEndDate);

      const updateData: Record<string, any> = {
        [statusKey]: 'applied',
        [appliedAtKey]: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      if (!originalEndDateAlreadySet && room.occupancyPeriod?.endDate) {
        updateData[originalEndDateKey] = Timestamp.fromDate(room.occupancyPeriod.endDate);
      }

      await updateDoc(doc(db, 'portville_residencies', room.currentResidencyId), updateData);

      alert(`${round}차 계약 연장 신청 상태로 변경되었습니다.`);
      window.location.reload();
    } catch (error) {
      console.error('계약 연장 신청 상태 변경 실패:', error);
      alert('계약 연장 신청 상태 변경 중 오류가 발생했습니다.');
    } finally {
      setMarkingExtensionApplied(null);
    }
  };

  const uploadLotteryDocument = async (type: DocumentType, file: File) => {
    if (!room?.id) return;
    if (!lotteryDocMeta?.id || !lotteryDocMeta?.uid) {
      setLotteryDocumentsError('추첨 정보를 찾을 수 없습니다. (추첨 기록이 없는 호실일 수 있습니다)');
      return;
    }

    const validationError = validateUploadFile(file);
    if (validationError) {
      setLotteryDocumentsError(validationError);
      return;
    }

    const existingPath = lotteryDocuments?.[type];
    try {
      setLotteryDocumentsUploading(type);
      setLotteryDocumentsError(null);

      const timestamp = Date.now();
      const safeOriginalName = file.name.split('/').join('_');
      const fileName = `${lotteryDocMeta.uid}_${type}_${timestamp}_${safeOriginalName}`;
      const storagePath = `portville_lottery_documents/${lotteryDocMeta.uid}/${lotteryDocMeta.id}/${fileName}`;
      const storageRef = ref(storage, storagePath);

      try {
        await uploadBytes(storageRef, file, { contentType: file.type });

        const updatedDocuments: Documents = {
          ...(lotteryDocuments || {}),
          [type]: storagePath
        };

        await updateDoc(doc(db, 'portville_lottery_history', lotteryDocMeta.id), {
          documents: updatedDocuments,
          documentsUpdatedAt: Timestamp.now()
        });

        setLotteryDocuments(updatedDocuments);

        if (existingPath && existingPath !== storagePath) {
          try {
            await deleteObject(ref(storage, existingPath));
          } catch (deleteError) {
            console.warn('기존 파일 삭제 실패:', deleteError);
            setLotteryDocumentsError('파일은 업로드되었지만, 기존 파일 삭제에 실패했습니다.');
          }
        }
      } catch (innerError) {
        try {
          await deleteObject(storageRef);
        } catch (cleanupError) {
          console.warn('업로드 실패 후 새 파일 정리 실패:', cleanupError);
        }
        throw innerError;
      }
    } catch (uploadError) {
      console.error('계약 전 제출 서류 업로드 실패:', uploadError);
      setLotteryDocumentsError('서류 업로드 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setLotteryDocumentsUploading(null);
    }
  };

  const uploadExtensionDocument = async (round: 1 | 2, type: DocumentType, file: File) => {
    if (!room?.currentResidencyId) return;

    const validationError = validateUploadFile(file);
    if (validationError) {
      setExtensionDocumentsError(validationError);
      return;
    }

    const existingPath = (round === 2 ? extensionDocumentsRound2 : extensionDocumentsRound1)?.[type];
    try {
      setExtensionDocumentsUploading({ round, type });
      setExtensionDocumentsError(null);

      const timestamp = Date.now();
      const safeOriginalName = file.name.split('/').join('_');
      const ownerSegment = room.residentUid || `manual_${room.currentResidencyId}`;
      const fileName = `${ownerSegment}_${type}_${timestamp}_${safeOriginalName}`;
      const storageBasePath = round === 2 ? 'portville_second_extension_documents' : 'portville_extension_documents';
      const storagePath = `${storageBasePath}/${ownerSegment}/${room.currentResidencyId}/${fileName}`;
      const storageRef = ref(storage, storagePath);

      const documentsKey = round === 2 ? 'secondExtensionDocuments' : 'extensionDocuments';
      const documentsUpdatedAtKey = round === 2 ? 'secondExtensionDocumentsUpdatedAt' : 'extensionDocumentsUpdatedAt';

      try {
        await uploadBytes(storageRef, file, { contentType: file.type });

        const nextDocuments: Documents = {
          ...((round === 2 ? extensionDocumentsRound2 : extensionDocumentsRound1) || {}),
          [type]: storagePath
        };

        await updateDoc(doc(db, 'portville_residencies', room.currentResidencyId), {
          [documentsKey]: nextDocuments,
          [documentsUpdatedAtKey]: Timestamp.now(),
          updatedAt: Timestamp.now()
        });

        if (round === 2) {
          setExtensionDocumentsRound2(nextDocuments);
        } else {
          setExtensionDocumentsRound1(nextDocuments);
        }

        if (existingPath && existingPath !== storagePath) {
          try {
            await deleteObject(ref(storage, existingPath));
          } catch (deleteError) {
            console.warn('기존 파일 삭제 실패:', deleteError);
            setExtensionDocumentsError('파일은 업로드되었지만, 기존 파일 삭제에 실패했습니다.');
          }
        }
      } catch (innerError) {
        try {
          await deleteObject(storageRef);
        } catch (cleanupError) {
          console.warn('업로드 실패 후 새 파일 정리 실패:', cleanupError);
        }
        throw innerError;
      }
    } catch (uploadError) {
      console.error('연장 신청 첨부파일 업로드 실패:', uploadError);
      setExtensionDocumentsError('서류 업로드 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setExtensionDocumentsUploading(null);
    }
  };

  const downloadMoveInInspectionAttachment = async (
    inspection: MoveInInspectionRecord,
    attachment: MoveInInspectionAttachment
  ) => {
    try {
      setMoveInInspectionDownloadingAttachmentId(`${inspection.id}-${attachment.id}`);
      const filenameBase = attachment.name.replace(/\.[^/.]+$/, '') || '첨부파일';
      await downloadStorageFile(
        attachment.storagePath,
        `${room?.roomNumber || inspection.roomNumber || ''}호_입주점검_${filenameBase}`
      );
    } catch (downloadError) {
      console.error('입주점검 첨부파일 다운로드 실패:', downloadError);
      setMoveInInspectionError('입주점검 첨부파일 다운로드 중 오류가 발생했습니다.');
    } finally {
      setMoveInInspectionDownloadingAttachmentId(null);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* 호실번호 */}
        <div>
          <label htmlFor="roomNumber" className="block text-sm font-medium text-gray-700 mb-2">
            호실번호 *
          </label>
          <input
            type="text"
            id="roomNumber"
            required
            value={formData.roomNumber}
            onChange={(e) => handleInputChange('roomNumber', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="예: 101"
          />
        </div>

        {/* 방 상태 */}
        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
            방 상태 *
          </label>
          <select
            id="status"
            required
            value={formData.status}
            onChange={(e) => handleInputChange('status', e.target.value as any)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {Object.entries(ROOM_STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* 입주중이거나 계약진행인 경우에만 표시되는 필드들 */}
      {showResidentInfo && (
        <>
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {isOccupied ? '입주자 정보' : '예정 입주자 정보'}
            </h3>

            <div className="flex flex-wrap items-center gap-4 mb-4">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="residentInputMode"
                  checked={residentInputMode === 'manual'}
                  onChange={() => handleResidentInputModeChange('manual')}
                />
                수기 입력
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="residentInputMode"
                  checked={residentInputMode === 'select'}
                  onChange={() => handleResidentInputModeChange('select')}
                />
                기존 사용자 선택
              </label>
              {residentInputMode === 'select' && (
                <button
                  type="button"
                  onClick={() => loadUserProfiles()}
                  disabled={userProfilesLoading}
                  className="text-sm text-blue-600 hover:text-blue-700 disabled:text-gray-400"
                >
                  {userProfilesLoading ? '불러오는 중...' : '사용자 목록 새로고침'}
                </button>
              )}
            </div>

            {residentInputMode === 'select' && (
              <div className="mb-6 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="userSearchTerm" className="block text-sm font-medium text-gray-700 mb-2">
                      사용자 검색
                    </label>
                    <input
                      id="userSearchTerm"
                      type="text"
                      value={userSearchTerm}
                      onChange={(e) => setUserSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="성명/이메일/연락처/소속/UID"
                    />
                  </div>

                  <div>
                    <label htmlFor="residentUid" className="block text-sm font-medium text-gray-700 mb-2">
                      기존 사용자 선택
                    </label>
                    <select
                      id="residentUid"
                      value={formData.residentUid || ''}
                      onChange={(e) => handleSelectResidentUid(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">선택 안 함</option>
                      {filteredUserProfiles.map((profile) => (
                        <option key={profile.uid} value={profile.uid}>
                          {profile.name}
                          {profile.phoneNumber ? ` / ${profile.phoneNumber}` : ''}
                          {profile.organization ? ` / ${profile.organization}` : ''}
                          {profile.email ? ` / ${profile.email}` : ''}
                        </option>
                      ))}
                    </select>
                    {userProfilesError && (
                      <div className="mt-1 text-sm text-red-600">{userProfilesError}</div>
                    )}
                  </div>
                </div>
                {formData.residentUid && (
                  <div className="rounded-md border border-blue-100 bg-blue-50 px-4 py-3 text-sm">
                    <div className="font-medium text-blue-900">
                      선택된 사용자
                    </div>
                    <div className="mt-1 text-blue-800 break-all">
                      이메일: {selectedUserProfile?.email || '이메일 정보 없음'}
                    </div>
                    <div className="mt-1 text-xs text-blue-700 break-all">
                      UID: {formData.residentUid}
                    </div>
                  </div>
                )}
                <div className="text-xs text-gray-500">
                  선택 시 성명/연락처/소속이 자동 입력되며, 필요하면 아래에서 수정할 수 있습니다.
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label htmlFor="residentName" className="block text-sm font-medium text-gray-700 mb-2">
                  성명 *
                </label>
                <input
                  type="text"
                  id="residentName"
                  required={showResidentInfo}
                  value={formData.residentName}
                  onChange={(e) => handleInputChange('residentName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label htmlFor="residentPhoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
                  연락처
                </label>
                <input
                  type="tel"
                  id="residentPhoneNumber"
                  value={formData.residentPhoneNumber}
                  onChange={(e) => handleInputChange('residentPhoneNumber', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="010-1234-5678"
                />
              </div>

              <div>
                <label htmlFor="residentOrganization" className="block text-sm font-medium text-gray-700 mb-2">
                  소속
                </label>
                <input
                  type="text"
                  id="residentOrganization"
                  value={formData.residentOrganization}
                  onChange={(e) => handleInputChange('residentOrganization', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* 입주기간 (입주중/계약진행) */}
          {showResidentInfo && (
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">입주기간</h3>
              {isOccupied && (
                <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-4">
                  <label className="flex items-start gap-3 text-sm text-blue-900">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={Boolean(formData.hasManualExtension1)}
                      disabled={extensionEditingLocked}
                      onChange={(e) => handleManualExtensionToggle(e.target.checked)}
                    />
                    <span>
                      <span className="block font-medium">이미 1차 연장 완료</span>
                      <span className="block text-xs text-blue-700 mt-1">
                        기존 입주자를 수기 등록할 때 최초 계약 종료일과 현재 연장 후 종료일을 함께 반영합니다.
                      </span>
                    </span>
                  </label>
                  {extensionEditingLocked && (
                    <div className="mt-3 text-xs text-blue-700">
                      실제 연장계약/2차 연장 이력이 있는 호실은 여기서 1차 연장 상태를 해제할 수 없습니다.
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-2">
                    입주 개시일 {isOccupied ? '*' : ''}
                  </label>
                  <input
                    type="date"
                    id="startDate"
                    required={isOccupied}
                    value={formData.startDate}
                    onChange={(e) => handleInputChange('startDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-2">
                    {formData.hasManualExtension1 ? '현재 계약 종료일' : '계약 종료일'} {isOccupied ? '*' : ''}
                  </label>
                  <input
                    type="date"
                    id="endDate"
                    required={isOccupied}
                    value={formData.endDate}
                    onChange={(e) => handleInputChange('endDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {isOccupied && formData.hasManualExtension1 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <div>
                    <label htmlFor="originalContractEndDate" className="block text-sm font-medium text-gray-700 mb-2">
                      최초 계약 종료일 *
                    </label>
                    <input
                      type="date"
                      id="originalContractEndDate"
                      required={Boolean(formData.hasManualExtension1)}
                      value={formData.originalContractEndDate || ''}
                      onChange={(e) => handleInputChange('originalContractEndDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      현재 계약 종료일보다 이전 날짜여야 합니다.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 관리자 추가 정보 (입주중이거나 계약진행인 경우에만 표시) */}
          {showResidentInfo && (
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">관리자 추가 정보</h3>
              <div className="space-y-6">
                {/* 동거인 */}
                <div className="rounded-md border border-gray-200 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">동거인</div>
                      <div className="mt-1 text-xs text-gray-500">
                        0명부터 최대 3명까지 등록할 수 있으며, 성명과 연락처는 필수입니다.
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={addRoommate}
                        disabled={(formData.roommates?.length || 0) >= MAX_PORTVILLE_ROOMMATES || isSubmitting}
                        className="h-9 min-w-9 rounded-md border border-blue-300 bg-blue-50 px-3 text-sm font-semibold text-blue-700 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
                        aria-label="동거인 추가"
                      >
                        +
                      </button>
                      <button
                        type="button"
                        onClick={() => removeRoommate(activeRoommateIndex)}
                        disabled={(formData.roommates?.length || 0) === 0 || isSubmitting}
                        className="h-9 min-w-9 rounded-md border border-red-300 bg-red-50 px-3 text-sm font-semibold text-red-700 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
                        aria-label="현재 동거인 삭제"
                      >
                        -
                      </button>
                    </div>
                  </div>

                  {formData.roommates?.[activeRoommateIndex]?.attachment ||
                  formData.roommates?.[activeRoommateIndex]?.pendingAttachmentFile ? (
                    <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                      첨부파일이 있는 동거인은 바로 삭제할 수 없습니다. 첨부파일을 먼저
                      삭제하거나 선택 취소한 후 - 버튼을 눌러주세요.
                    </div>
                  ) : null}

                  {(formData.roommates?.length || 0) === 0 ? (
                    <div className="rounded-md bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
                      등록된 동거인이 없습니다. + 버튼으로 동거인을 추가하세요.
                    </div>
                  ) : (
                    <>
                      <div className="mb-4 flex gap-2 border-b border-gray-200">
                        {(formData.roommates || []).map((_, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => {
                              setActiveRoommateIndex(index);
                              setRoommateAttachmentError(null);
                            }}
                            className={`border-b-2 px-4 py-2 text-sm font-medium ${
                              activeRoommateIndex === index
                                ? 'border-blue-600 text-blue-700'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            동거인 {index + 1}
                          </button>
                        ))}
                      </div>

                      {formData.roommates?.[activeRoommateIndex] && (
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                              <label
                                htmlFor={`roommate-name-${activeRoommateIndex}`}
                                className="mb-2 block text-sm font-medium text-gray-700"
                              >
                                성명 *
                              </label>
                              <input
                                id={`roommate-name-${activeRoommateIndex}`}
                                type="text"
                                required
                                value={formData.roommates[activeRoommateIndex].name}
                                onChange={(event) =>
                                  updateRoommate(activeRoommateIndex, { name: event.target.value })
                                }
                                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="동거인 성명"
                              />
                            </div>
                            <div>
                              <label
                                htmlFor={`roommate-contact-${activeRoommateIndex}`}
                                className="mb-2 block text-sm font-medium text-gray-700"
                              >
                                연락처 *
                              </label>
                              <input
                                id={`roommate-contact-${activeRoommateIndex}`}
                                type="tel"
                                required
                                value={formData.roommates[activeRoommateIndex].contact}
                                onChange={(event) =>
                                  updateRoommate(activeRoommateIndex, { contact: event.target.value })
                                }
                                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="010-1234-5678"
                              />
                            </div>
                          </div>

                          <div>
                            <div className="mb-2 text-sm font-medium text-gray-700">
                              첨부파일 (선택, 최대 1개)
                            </div>
                            <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                              {formData.roommates[activeRoommateIndex].pendingAttachmentFile ? (
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                  <div className="min-w-0">
                                    <div className="truncate text-sm font-medium text-gray-900">
                                      {formData.roommates[activeRoommateIndex].pendingAttachmentFile?.name}
                                    </div>
                                    <div className="text-xs text-blue-600">
                                      저장 시 {formData.roommates[activeRoommateIndex].attachment ? '기존 파일을 교체합니다.' : '업로드됩니다.'}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeRoommateAttachment(activeRoommateIndex)}
                                    className="text-sm font-medium text-red-600 hover:text-red-700"
                                  >
                                    선택 취소
                                  </button>
                                </div>
                              ) : formData.roommates[activeRoommateIndex].attachment ? (
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                  <div className="min-w-0">
                                    <div className="truncate text-sm font-medium text-gray-900">
                                      {formData.roommates[activeRoommateIndex].attachment?.name}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {((formData.roommates[activeRoommateIndex].attachment?.size || 0) / 1024 / 1024).toFixed(2)}MB
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <button
                                      type="button"
                                      onClick={() => downloadRoommateAttachment(activeRoommateIndex)}
                                      disabled={roommateAttachmentDownloadingIndex === activeRoommateIndex}
                                      className="text-sm font-medium text-blue-600 hover:text-blue-700 disabled:text-gray-400"
                                    >
                                      {roommateAttachmentDownloadingIndex === activeRoommateIndex ? '다운로드 중...' : '다운로드'}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => removeRoommateAttachment(activeRoommateIndex)}
                                      className="text-sm font-medium text-red-600 hover:text-red-700"
                                    >
                                      삭제
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-sm text-gray-500">등록된 첨부파일이 없습니다.</div>
                              )}
                            </div>

                            <label className="mt-3 inline-flex cursor-pointer items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                              {formData.roommates[activeRoommateIndex].attachment ? '교체 파일 선택' : '파일 선택'}
                              <input
                                type="file"
                                className="hidden"
                                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                                disabled={isSubmitting}
                                onChange={(event) => {
                                  const file = event.target.files?.[0];
                                  if (file) selectRoommateAttachment(activeRoommateIndex, file);
                                  event.target.value = '';
                                }}
                              />
                            </label>
                            <div className="mt-2 text-xs text-gray-500">
                              PDF, JPG, PNG / 최대 5MB
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {roommateAttachmentError && (
                    <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                      {roommateAttachmentError}
                    </div>
                  )}
                </div>

                {/* 차량정보 */}
                <div>
                  <label htmlFor="vehicleInfo" className="block text-sm font-medium text-gray-700 mb-2">
                    차량정보
                  </label>
                  <textarea
                    id="vehicleInfo"
                    rows={3}
                    value={formData.vehicleInfo}
                    onChange={(e) => handleInputChange('vehicleInfo', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="차량정보를 입력하세요"
                  />
                </div>

                {/* 비고 */}
                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                    비고
                  </label>
                  <textarea
                    id="notes"
                    rows={4}
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="기타 특이사항이나 관리자 메모를 입력하세요"
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {room && hasCurrentResidency && room.status === 'occupied' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h3 className="text-sm font-medium text-amber-900">관리자 연장 신청 접수</h3>
              <p className="mt-1 text-sm text-amber-700">
                사용자를 대신해 계약 연장 신청 상태로 변경할 수 있습니다.
              </p>
              <div className="mt-2 text-xs text-amber-700">
                1차 상태: {room.extensionStatus || 'none'} / 2차 상태: {room.secondExtensionStatus || 'none'}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => handleMarkExtensionApplied(1)}
                disabled={!canMarkFirstExtensionApplied || Boolean(markingExtensionApplied)}
                className={`px-4 py-2 text-sm font-medium rounded-md border ${
                  canMarkFirstExtensionApplied && !markingExtensionApplied
                    ? 'bg-white text-amber-800 border-amber-300 hover:bg-amber-100'
                    : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                }`}
              >
                {markingExtensionApplied === 1 ? '처리 중...' : '1차 연장신청 상태로 변경'}
              </button>
              <button
                type="button"
                onClick={() => handleMarkExtensionApplied(2)}
                disabled={!canMarkSecondExtensionApplied || Boolean(markingExtensionApplied)}
                className={`px-4 py-2 text-sm font-medium rounded-md border ${
                  canMarkSecondExtensionApplied && !markingExtensionApplied
                    ? 'bg-white text-amber-800 border-amber-300 hover:bg-amber-100'
                    : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                }`}
              >
                {markingExtensionApplied === 2 ? '처리 중...' : '2차 연장신청 상태로 변경'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 계약서 보기 버튼 (수정 모드이고 계약이 완료된 경우에만 표시) */}
      {room && room.contractSignedAt && onViewContract && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-green-800">계약 완료</h3>
              <p className="text-sm text-green-600">
                {room.contractSignedAt.toLocaleDateString('ko-KR')}에 계약이 완료되었습니다.
              </p>
            </div>
            <button
              type="button"
              onClick={() => onViewContract(room, 'original')}
              className="px-4 py-2 text-sm font-medium text-green-700 bg-white border border-green-300 rounded-md hover:bg-green-50"
            >
              계약서 보기
            </button>
          </div>
        </div>
      )}

      {/* 연장계약서 보기 버튼 (수정 모드이고 연장계약이 완료된 경우에만 표시) */}
      {room && room.extensionContractSignedAt && onViewContract && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-blue-800">1차 연장계약 완료</h3>
              <p className="text-sm text-blue-600">
                {room.extensionContractSignedAt.toLocaleDateString('ko-KR')}에 1차 연장계약이 완료되었습니다.
              </p>
              {room.originalEndDate && (
                <p className="text-xs text-blue-500 mt-1">
                  원래 계약 종료일: {room.originalEndDate.toLocaleDateString('ko-KR')}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => onViewContract(room, 'extension1')}
              className="px-4 py-2 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-md hover:bg-blue-50"
            >
              1차 연장계약서 보기
            </button>
          </div>
        </div>
      )}

      {room && room.secondExtensionContractSignedAt && onViewContract && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-indigo-800">2차 연장계약 완료</h3>
              <p className="text-sm text-indigo-600">
                {room.secondExtensionContractSignedAt.toLocaleDateString('ko-KR')}에 2차 연장계약이 완료되었습니다.
              </p>
              {room.secondOriginalEndDate && (
                <p className="text-xs text-indigo-500 mt-1">
                  2차 연장 전 계약 종료일: {room.secondOriginalEndDate.toLocaleDateString('ko-KR')}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => onViewContract(room, 'extension2')}
              className="px-4 py-2 text-sm font-medium text-indigo-700 bg-white border border-indigo-300 rounded-md hover:bg-indigo-50"
            >
              2차 연장계약서 보기
            </button>
          </div>
        </div>
      )}

      {room && hasCurrentResidency && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900">입주점검 기록</h3>
              <p className="text-sm text-gray-600 mt-1">
                계약 전 입주점검 완료 내역과 항목별 메모, 첨부파일을 확인할 수 있습니다.
              </p>
            </div>
            {moveInInspectionLoading && <div className="text-sm text-gray-500">불러오는 중...</div>}
          </div>

          {moveInInspectionError && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-sm">
              {moveInInspectionError}
            </div>
          )}

          {!moveInInspectionLoading && moveInInspectionRecords.length === 0 && !moveInInspectionError && (
            <div className="mt-4 text-sm text-gray-500 border border-dashed border-gray-300 rounded p-4">
              등록된 입주점검 기록이 없습니다.
            </div>
          )}

          <div className="mt-4 space-y-4">
            {moveInInspectionRecords.map((inspection, index) => {
              const signedAt = inspection.inspectionSignedAt || inspection.createdAt;

              return (
                <div key={inspection.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="text-sm font-semibold text-gray-900">
                          입주점검 {moveInInspectionRecords.length > 1 ? `#${moveInInspectionRecords.length - index}` : ''}
                        </h4>
                        {index === 0 && (
                          <span className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                            최신
                          </span>
                        )}
                      </div>
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-700">
                        <div>
                          <span className="font-medium text-gray-600">완료일:</span>{' '}
                          {signedAt ? signedAt.toLocaleString('ko-KR') : '정보 없음'}
                        </div>
                        <div>
                          <span className="font-medium text-gray-600">점검자:</span>{' '}
                          {inspection.inspectorName || '정보 없음'}
                        </div>
                      </div>
                    </div>

                    {inspection.signatureUrl && (
                      <div className="w-full lg:w-48">
                        <div className="text-xs font-medium text-gray-600 mb-1">서명</div>
                        <div className="border border-gray-200 rounded bg-white p-2">
                          <img
                            src={inspection.signatureUrl}
                            alt="입주점검 서명"
                            className="max-h-24 w-full object-contain"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-4">
                    <div className="text-xs font-medium text-gray-600 mb-2">점검 항목</div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                      {inspection.inspectionItems.length === 0 ? (
                        <div className="text-sm text-gray-500 border border-dashed border-gray-300 rounded p-3 bg-white">
                          저장된 점검 항목이 없습니다.
                        </div>
                      ) : (
                        inspection.inspectionItems.map((item) => (
                          <div key={item.id} className="border border-gray-200 rounded p-3 bg-white">
                            <div className="flex items-center justify-between gap-3">
                              <div className="text-sm font-medium text-gray-900">{item.name}</div>
                              <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${
                                item.checked ? 'text-green-700 bg-green-100' : 'text-gray-600 bg-gray-100'
                              }`}>
                                {item.checked ? '확인완료' : '미확인'}
                              </span>
                            </div>
                            <div className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">
                              {item.notes?.trim() ? item.notes : <span className="text-gray-400">메모 없음</span>}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {inspection.inspectionNotes?.trim() && (
                    <div className="mt-4">
                      <div className="text-xs font-medium text-gray-600 mb-2">종합 메모</div>
                      <div className="text-sm text-gray-700 whitespace-pre-wrap border border-gray-200 rounded p-3 bg-white">
                        {inspection.inspectionNotes}
                      </div>
                    </div>
                  )}

                  <div className="mt-4">
                    <div className="text-xs font-medium text-gray-600 mb-2">첨부파일</div>
                    {inspection.attachments.length === 0 ? (
                      <div className="text-sm text-gray-500 border border-dashed border-gray-300 rounded p-3 bg-white">
                        등록된 첨부파일이 없습니다.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {inspection.attachments.map((attachment) => {
                          const downloadKey = `${inspection.id}-${attachment.id}`;
                          const isDownloading = moveInInspectionDownloadingAttachmentId === downloadKey;

                          return (
                            <div
                              key={downloadKey}
                              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border border-gray-200 rounded p-3 bg-white"
                            >
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">{attachment.name}</div>
                                <div className="text-xs text-gray-500">
                                  {(attachment.size / 1024 / 1024).toFixed(2)}MB
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => downloadMoveInInspectionAttachment(inspection, attachment)}
                                disabled={Boolean(moveInInspectionDownloadingAttachmentId)}
                                className={`px-3 py-1.5 rounded text-sm ${
                                  Boolean(moveInInspectionDownloadingAttachmentId)
                                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                    : 'bg-[#155acb] text-white hover:bg-[#155acb]/90'
                                }`}
                              >
                                {isDownloading ? '확인 중...' : '확인'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 첨부파일 (계약 전 제출 서류 / 연장 신청 서류) */}
	      {room && hasCurrentResidency && room.status !== 'preparing' && (
	        <div className="bg-white border border-gray-200 rounded-lg p-6">
	          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
	            <div>
	              <h3 className="text-lg font-medium text-gray-900">첨부파일</h3>
              <p className="text-sm text-gray-600 mt-1">
                추첨 직후 업로드 서류 및 연장 신청 첨부파일을 확인/다운로드할 수 있습니다.
              </p>
            </div>
          </div>

          {/* 계약 전 제출 서류(추첨 후 업로드) */}
          <div className="mt-6">
            <div className="flex items-center justify-between gap-4">
              <div className="text-sm font-medium text-gray-900">계약 전 제출 서류(추첨 후 업로드)</div>
              {lotteryDocumentsLoading && <div className="text-sm text-gray-500">불러오는 중...</div>}
              {lotteryDocumentsDownloading && <div className="text-sm text-gray-500">다운로드 중...</div>}
              {lotteryDocumentsUploading && <div className="text-sm text-gray-500">업로드 중...</div>}
            </div>

            {lotteryDocumentsError && (
              <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-sm">
                {lotteryDocumentsError}
              </div>
            )}

            <div className="mt-3 space-y-2">
              {(['identification', 'employment', 'other'] as const).map((type) => {
                const storagePath = lotteryDocuments[type];
                const isRequired = type === 'identification' || type === 'employment';
                const isDownloading = lotteryDocumentsDownloading === type;
                const isUploading = lotteryDocumentsUploading === type;

                return (
                  <div
                    key={type}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border border-gray-200 rounded p-3 bg-gray-50"
                  >
                    <div className="text-sm text-gray-800">
                      <span className="font-medium">{getDocumentDisplayName(type)}</span>
                      {isRequired && <span className="ml-2 text-xs text-red-600">(필수)</span>}
                      {storagePath ? (
                        <span className="ml-2 text-xs text-gray-500">
                          {getOriginalFileNameFromStoragePath(storagePath)}
                        </span>
                      ) : (
                        <span className="ml-2 text-xs text-gray-500">미제출</span>
                      )}
                    </div>

                    <div className="flex gap-2">
                      {!storagePath ? null : (
                        <button
                          type="button"
                          onClick={async () => {
                            if (!storagePath) return;
                            try {
                              setLotteryDocumentsDownloading(type);
                              await downloadStorageFile(
                                storagePath,
                                `${room.roomNumber}호_${residentNameForFiles}_계약전서류_${getDocumentDisplayName(type)}`
                              );
                            } catch (downloadError) {
                              console.error('계약 전 제출 서류 다운로드 실패:', downloadError);
                              setLotteryDocumentsError('서류 다운로드 중 오류가 발생했습니다.');
                            } finally {
                              setLotteryDocumentsDownloading(null);
                            }
                          }}
                          disabled={Boolean(lotteryDocumentsDownloading) || Boolean(lotteryDocumentsLoading)}
                          className={`px-3 py-1.5 rounded text-sm ${
                            Boolean(lotteryDocumentsDownloading) || Boolean(lotteryDocumentsLoading)
                              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                              : 'bg-[#155acb] text-white hover:bg-[#155acb]/90'
                          }`}
                        >
                          {isDownloading ? '다운로드 중...' : '다운로드'}
                        </button>
                      )}

                      <label
                        className={`px-3 py-1.5 rounded text-sm cursor-pointer ${
                          Boolean(lotteryDocumentsUploading) || Boolean(lotteryDocumentsLoading)
                            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                            : 'bg-slate-700 text-white hover:bg-slate-700/90'
                        }`}
                      >
                        <input
                          type="file"
                          className="hidden"
                          accept="application/pdf,image/jpeg,image/jpg,image/png,.pdf,.jpg,.jpeg,.png"
                          disabled={Boolean(lotteryDocumentsUploading) || Boolean(lotteryDocumentsLoading)}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            e.target.value = '';
                            if (!file) return;
                            await uploadLotteryDocument(type, file);
                          }}
                        />
                        {isUploading ? '업로드 중...' : storagePath ? '교체 업로드' : '업로드'}
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 연장 신청 첨부파일 */}
          {(showExtensionDocumentsSection || showSecondExtensionDocumentsSection) && (
            <div className="mt-8">
              <div className="flex items-center justify-between gap-4">
                <div className="text-sm font-medium text-gray-900">연장 신청 첨부파일</div>
                {extensionDocumentsDownloading && <div className="text-sm text-gray-500">다운로드 중...</div>}
                {extensionDocumentsUploading && <div className="text-sm text-gray-500">업로드 중...</div>}
              </div>

              {extensionDocumentsError && (
                <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-sm">
                  {extensionDocumentsError}
                </div>
              )}

              <div className="mt-3 space-y-6">
                {showExtensionDocumentsSection && (
                  <div>
                    <div className="text-xs font-medium text-gray-600 mb-2">1차 연장 신청</div>
                    <div className="space-y-2">
                      {(['identification', 'employment', 'other'] as const).map((type) => {
                        const storagePath = extensionDocumentsRound1?.[type];
                        const isRequired = type === 'identification' || type === 'employment';
                        const isDownloading =
                          extensionDocumentsDownloading?.round === 1 && extensionDocumentsDownloading?.type === type;
                        const isUploading =
                          extensionDocumentsUploading?.round === 1 && extensionDocumentsUploading?.type === type;

                        return (
                          <div
                            key={`ext1-${type}`}
                            className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border border-gray-200 rounded p-3 bg-gray-50"
                          >
                            <div className="text-sm text-gray-800">
                              <span className="font-medium">{getDocumentDisplayName(type)}</span>
                              {isRequired && <span className="ml-2 text-xs text-red-600">(필수)</span>}
                              {storagePath ? (
                                <span className="ml-2 text-xs text-gray-500">
                                  {getOriginalFileNameFromStoragePath(storagePath)}
                                </span>
                              ) : (
                                <span className="ml-2 text-xs text-gray-500">미제출</span>
                              )}
                            </div>

                            <div className="flex gap-2">
                              {!storagePath ? null : (
                                <button
                                  type="button"
                                  onClick={async () => {
                                    if (!storagePath) return;
                                    try {
                                      setExtensionDocumentsDownloading({ round: 1, type });
                                      await downloadStorageFile(
                                        storagePath,
                                        `${room.roomNumber}호_${residentNameForFiles}_1차연장신청_${getDocumentDisplayName(type)}`
                                      );
                                    } catch (downloadError) {
                                      console.error('1차 연장 신청 첨부파일 다운로드 실패:', downloadError);
                                      setExtensionDocumentsError('서류 다운로드 중 오류가 발생했습니다.');
                                    } finally {
                                      setExtensionDocumentsDownloading(null);
                                    }
                                  }}
                                  disabled={Boolean(extensionDocumentsDownloading)}
                                  className={`px-3 py-1.5 rounded text-sm ${
                                    Boolean(extensionDocumentsDownloading)
                                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                      : 'bg-[#155acb] text-white hover:bg-[#155acb]/90'
                                  }`}
                                >
                                  {isDownloading ? '다운로드 중...' : '다운로드'}
                                </button>
                              )}

                              <label
                                className={`px-3 py-1.5 rounded text-sm cursor-pointer ${
                                  Boolean(extensionDocumentsUploading)
                                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                    : 'bg-slate-700 text-white hover:bg-slate-700/90'
                                }`}
                              >
                                <input
                                  type="file"
                                  className="hidden"
                                  accept="application/pdf,image/jpeg,image/jpg,image/png,.pdf,.jpg,.jpeg,.png"
                                  disabled={Boolean(extensionDocumentsUploading)}
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    e.target.value = '';
                                    if (!file) return;
                                    await uploadExtensionDocument(1, type, file);
                                  }}
                                />
                                {isUploading ? '업로드 중...' : storagePath ? '교체 업로드' : '업로드'}
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {showSecondExtensionDocumentsSection && (
                  <div>
                    <div className="text-xs font-medium text-gray-600 mb-2">2차 연장 신청</div>
                    <div className="space-y-2">
                      {(['identification', 'employment', 'other'] as const).map((type) => {
                        const storagePath = extensionDocumentsRound2?.[type];
                        const isRequired = type === 'identification' || type === 'employment';
                        const isDownloading =
                          extensionDocumentsDownloading?.round === 2 && extensionDocumentsDownloading?.type === type;
                        const isUploading =
                          extensionDocumentsUploading?.round === 2 && extensionDocumentsUploading?.type === type;

                        return (
                          <div
                            key={`ext2-${type}`}
                            className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border border-gray-200 rounded p-3 bg-gray-50"
                          >
                            <div className="text-sm text-gray-800">
                              <span className="font-medium">{getDocumentDisplayName(type)}</span>
                              {isRequired && <span className="ml-2 text-xs text-red-600">(필수)</span>}
                              {storagePath ? (
                                <span className="ml-2 text-xs text-gray-500">
                                  {getOriginalFileNameFromStoragePath(storagePath)}
                                </span>
                              ) : (
                                <span className="ml-2 text-xs text-gray-500">미제출</span>
                              )}
                            </div>

                            <div className="flex gap-2">
                              {!storagePath ? null : (
                                <button
                                  type="button"
                                  onClick={async () => {
                                    if (!storagePath) return;
                                    try {
                                      setExtensionDocumentsDownloading({ round: 2, type });
                                      await downloadStorageFile(
                                        storagePath,
                                        `${room.roomNumber}호_${residentNameForFiles}_2차연장신청_${getDocumentDisplayName(type)}`
                                      );
                                    } catch (downloadError) {
                                      console.error('2차 연장 신청 첨부파일 다운로드 실패:', downloadError);
                                      setExtensionDocumentsError('서류 다운로드 중 오류가 발생했습니다.');
                                    } finally {
                                      setExtensionDocumentsDownloading(null);
                                    }
                                  }}
                                  disabled={Boolean(extensionDocumentsDownloading)}
                                  className={`px-3 py-1.5 rounded text-sm ${
                                    Boolean(extensionDocumentsDownloading)
                                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                      : 'bg-[#155acb] text-white hover:bg-[#155acb]/90'
                                  }`}
                                >
                                  {isDownloading ? '다운로드 중...' : '다운로드'}
                                </button>
                              )}

                              <label
                                className={`px-3 py-1.5 rounded text-sm cursor-pointer ${
                                  Boolean(extensionDocumentsUploading)
                                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                    : 'bg-slate-700 text-white hover:bg-slate-700/90'
                                }`}
                              >
                                <input
                                  type="file"
                                  className="hidden"
                                  accept="application/pdf,image/jpeg,image/jpg,image/png,.pdf,.jpg,.jpeg,.png"
                                  disabled={Boolean(extensionDocumentsUploading)}
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    e.target.value = '';
                                    if (!file) return;
                                    await uploadExtensionDocument(2, type, file);
                                  }}
                                />
                                {isUploading ? '업로드 중...' : storagePath ? '교체 업로드' : '업로드'}
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 퇴거 승인 정보 (퇴거가 승인된 경우에만 표시) */}
      {room && room.moveOutInfo && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <svg className="w-6 h-6 text-orange-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <h3 className="text-lg font-medium text-orange-800">퇴거 승인됨 - 계약 종료일 변경</h3>
            </div>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
              계약 종료일 변경됨
            </span>
          </div>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-orange-700">원래 계약 종료일</p>
                <p className="text-sm text-orange-600">
                  {room.moveOutInfo.originalEndDate.toLocaleDateString('ko-KR')}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-orange-700">변경된 계약 종료일</p>
                <p className="text-sm text-orange-600 font-semibold">
                  {room.moveOutInfo.approvedMoveOutDate.toLocaleDateString('ko-KR')}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-orange-700">퇴거 사유</p>
                <p className="text-sm text-orange-600">
                  {room.moveOutInfo.moveOutReason === 'personnel_change' ? '인사변경' : '개인사유'}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-orange-700">승인일</p>
                <p className="text-sm text-orange-600">
                  {room.moveOutInfo.approvedAt.toLocaleDateString('ko-KR')}
                </p>
              </div>
            </div>
            
            {room.moveOutInfo.notes && (
              <div className="pt-4 border-t border-orange-200">
                <p className="text-sm font-medium text-orange-700">승인 메모</p>
                <p className="text-sm text-orange-600 bg-white p-3 rounded border mt-2">
                  {room.moveOutInfo.notes}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 퇴거신청 정보 (대기중인 퇴거신청이 있는 경우에만 표시) */}
      {room && (room as any).hasPendingMoveOut && (room as any).moveOutRequest && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <svg className="w-6 h-6 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <h3 className="text-lg font-medium text-red-800">퇴거 신청 대기중</h3>
            </div>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
              검토 필요
            </span>
          </div>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-red-700">신청자</p>
                <p className="text-sm text-red-600">{(room as any).moveOutRequest.residentInfo?.name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-red-700">신청일</p>
                <p className="text-sm text-red-600">
                  {(room as any).moveOutRequest.appliedAt?.toDate?.()?.toLocaleDateString('ko-KR') || 
                   new Date((room as any).moveOutRequest.appliedAt.seconds * 1000).toLocaleDateString('ko-KR')}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-red-700">퇴거 사유</p>
                <p className="text-sm text-red-600">
                  {(room as any).moveOutRequest.moveOutReason === 'personnel_change' ? '인사변경' : '개인사유'}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-red-700">퇴거 희망일</p>
                <p className="text-sm text-red-600">
                  {(room as any).moveOutRequest.desiredMoveOutDate?.toDate?.()?.toLocaleDateString('ko-KR') || 
                   new Date((room as any).moveOutRequest.desiredMoveOutDate.seconds * 1000).toLocaleDateString('ko-KR')}
                </p>
              </div>
            </div>
            
            <div className="pt-4 border-t border-red-200">
              <div className="flex space-x-3">
                <button
                  type="button"
                  disabled={processingMoveOut}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => {
                    if (onMoveOutAction && (room as any).moveOutRequest?.id && !processingMoveOut) {
                      onMoveOutAction('approve', (room as any).moveOutRequest.id);
                    }
                  }}
                >
                  {processingMoveOut ? '처리 중...' : '승인'}
                </button>
                <button
                  type="button"
                  disabled={processingMoveOut}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => {
                    if (onMoveOutAction && (room as any).moveOutRequest?.id && !processingMoveOut) {
                      onMoveOutAction('reject', (room as any).moveOutRequest.id);
                    }
                  }}
                >
                  {processingMoveOut ? '처리 중...' : '반려'}
                </button>
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50"
                  onClick={() => {
                    if (onMoveOutAction && (room as any).moveOutRequest?.id) {
                      onMoveOutAction('detail', (room as any).moveOutRequest.id);
                    }
                  }}
                >
                  상세보기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 버튼 */}
      <div className="flex justify-end space-x-4 pt-6 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          disabled={isSubmitting}
        >
          취소
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {isSubmitting ? '저장 중...' : (room ? '수정' : '추가')}
        </button>
      </div>
    </form>
  );
};

export default RoomForm; 
